import type { ReactNode } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/format";
import type { SuretyConsortiumAgreement, SuretyGuarantee } from "@/types/surety";
import { Chip, EmptyNote } from "./SuretyUI";

const COVERS_LABEL: Record<string, string> = {
  FIEL_CUMPLIMIENTO: "Fiel cumplimiento",
  ADELANTO_DIRECTO: "Adelanto directo",
  ADELANTO_MATERIALES: "Adelanto de materiales",
  OTRO: "Otra garantía",
};

/** Cómo se llama lo que cubre una garantía. */
export function coversLabel(guarantee: SuretyGuarantee) {
  if (guarantee.type === "RETENCION") return "Retención (sin fianza)";
  return guarantee.covers ? (COVERS_LABEL[guarantee.covers] ?? guarantee.covers) : "Garantía";
}

/** Días que faltan para una fecha AAAA-MM-DD. */
export function daysUntil(date: string) {
  return dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
}

/** Integrantes con su % y quién tramita las garantías, del contrato de consorcio. */
function ConsortiumShares({ agreement }: { agreement: SuretyConsortiumAgreement }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {agreement.members.map((member) => (
        <Chip key={`${member.ruc}-${member.name}`} tone="info">
          {member.name}
          {member.share !== null ? ` · ${member.share}%` : ""}
        </Chip>
      ))}
      {agreement.guaranteeManager && (
        <span className="text-xs text-slate-500">
          Tramita las fianzas: {agreement.guaranteeManager.name}
        </span>
      )}
    </div>
  );
}

/**
 * Garantías con su vencimiento, emisor y corredor. `action` pinta lo que se puede
 * hacer con cada una (ofrecer la renovación, agendarla...).
 */
export function GuaranteeTable({
  guarantees,
  empty,
  action,
}: {
  guarantees: SuretyGuarantee[];
  empty: string;
  action?: (guarantee: SuretyGuarantee) => ReactNode;
}) {
  if (guarantees.length === 0) return <EmptyNote>{empty}</EmptyNote>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-slate-500">
            <th scope="col" className="py-2 pr-3 font-semibold">Vence</th>
            <th scope="col" className="py-2 pr-3 font-semibold">Obra · afianzado</th>
            <th scope="col" className="py-2 pr-3 font-semibold">Emisor</th>
            <th scope="col" className="py-2 pr-3 text-right font-semibold">Monto</th>
            {action && <th scope="col" className="py-2"><span className="sr-only">Acción</span></th>}
          </tr>
        </thead>
        <tbody>
          {guarantees.map((guarantee, index) => {
            const days = guarantee.validUntil ? daysUntil(guarantee.validUntil) : null;
            return (
              <tr key={`${guarantee.contractingId}-${index}`} className="border-b last:border-0">
                <td className="py-3 pr-3 align-top">
                  <span
                    className={cn(
                      "font-bold",
                      days !== null && days <= 45 ? "text-amber-700" : "text-slate-800 dark:text-slate-100",
                    )}
                  >
                    {guarantee.validUntil ? dayjs(guarantee.validUntil).format("DD MMM YYYY") : "—"}
                  </span>
                  {days !== null && (
                    <span className="block text-xs text-slate-500">en {days} días</span>
                  )}
                </td>
                <td className="py-3 pr-3 align-top">
                  <Link
                    to={`/contracting/${guarantee.contractingId}`}
                    className="cursor-pointer font-semibold text-slate-800 hover:text-primary dark:text-slate-100"
                  >
                    {guarantee.bondholder ?? guarantee.nomenclature}
                  </Link>
                  <span className="block text-xs text-slate-500">
                    {[guarantee.nomenclature, guarantee.province, guarantee.department]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {guarantee.consortium && <ConsortiumShares agreement={guarantee.consortium} />}
                </td>
                <td className="py-3 pr-3 align-top">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                    {guarantee.issuer ?? "—"}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {coversLabel(guarantee)}
                    {guarantee.broker ? ` · vía ${guarantee.broker}` : ""}
                  </span>
                  {guarantee.issuedByUs && (
                    <span className="mt-1 inline-block">
                      <Chip tone="ok">La emitiste tú</Chip>
                    </span>
                  )}
                </td>
                <td className="py-3 pr-3 text-right align-top font-bold text-slate-900 dark:text-slate-50">
                  {formatMoney(guarantee.amount)}
                </td>
                {action && <td className="py-3 text-right align-top">{action(guarantee)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
